'use client';

import { motion } from 'framer-motion';
import { ArrowRight } from '../../icons/ArrowRight';
import type { UseCaseCardsSectionData } from '../../types';
import { MediaAsset } from '../MediaAsset';
import { PortableTextContent } from '../PortableTextContent';
import { SectionShell, staggerContainer, staggerItem } from '../section-shared';

export function CardGrid({ section }: { section: UseCaseCardsSectionData }) {
  return (
    <SectionShell>
      <div className="w-full text-center">
        <h2 className="font-semibold text-[36px] text-white leading-[1.2] sm:text-[28px]">{section.title}</h2>
        <PortableTextContent className="mt-4" value={section.description} />
      </div>

      <motion.div
        className="mt-10 grid grid-cols-3 gap-8 sm:mt-8 sm:grid-cols-1 sm:gap-6"
        initial="hidden"
        variants={staggerContainer}
        viewport={{ once: true, margin: '-60px' }}
        whileInView="visible"
      >
        {section.cards?.map((card) => (
          <motion.a
            key={card._key || card.title}
            className="flex flex-col gap-9"
            href={card.link?.href || '#'}
            rel="noreferrer"
            target="_blank"
            variants={staggerItem}
          >
            <div className="group relative overflow-hidden rounded-2xl bg-white/10">
              <MediaAsset className="aspect-[4/3] w-full object-cover" media={card.media} />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center bg-gradient-to-b from-transparent to-black/60 px-5 py-5 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <span className="flex size-[42px] items-center justify-center rounded-full border border-white/10 bg-black/60 shadow-[0_2px_8px_rgba(0,0,0,0.2)] backdrop-blur-[4px]">
                  <ArrowRight className="size-5 text-white" />
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <h3 className="font-semibold text-white text-xl leading-6 tracking-[-0.1px]">{card.title}</h3>
              <PortableTextContent value={card.description} />
            </div>
          </motion.a>
        ))}
      </motion.div>

      {section.button?.href && section.button?.label && (
        <div className="mt-10 flex justify-center">
          <a
            className="inline-flex items-center justify-center rounded-lg bg-[#0086FF] px-6 py-3 font-medium text-base text-white/95 transition-colors hover:bg-[#0f91ff]"
            href={section.button.href}
            rel={/^https?:\/\//.test(section.button.href) ? 'noreferrer' : undefined}
            target={/^https?:\/\//.test(section.button.href) ? '_blank' : undefined}
          >
            {section.button.label}
          </a>
        </div>
      )}
    </SectionShell>
  );
}
